import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useShell } from "./useShell";
import { ShellGuard } from "./ShellGuard";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const AdminRoutes = lazy(() =>
  import("./routes/AdminRoutes").then((m) => ({
    default: m.AdminRoutes,
  })),
);

const OpsRoutes = lazy(() =>
  import("./routes/OpsRoutes").then((m) => ({
    default: m.OpsRoutes,
  })),
);

const ClientRoutes = lazy(() =>
  import("./routes/ClientRoutes").then((m) => ({
    default: m.ClientRoutes,
  })),
);

/** Resolves the root "/" to the user's active shell.
 *  Waits for shell access to be resolved before redirecting,
 *  preventing premature redirects to the wrong shell during bootstrap. */
function ShellRedirect() {
  const { activeShell, isResolved } = useShell();
  const location = useLocation();

  // Wait for bootstrap to complete before redirecting
  if (!isResolved) {
    return <LoadingSpinner />;
  }

  // If already on a valid shell path, let React Router handle it — don't redirect
  const path = location.pathname;
  if (
    path.startsWith("/admin") ||
    path.startsWith("/ops") ||
    path.startsWith("/client") ||
    path.startsWith("/hub")
  ) {
    return null;
  }

  switch (activeShell) {
    case "admin":
      return <Navigate to="/admin" replace />;
    case "ops":
      return <Navigate to="/ops" replace />;
    case "client":
      return <Navigate to="/client" replace />;
  }
}

export function ShellRouter() {
  return (
    <Routes>
      {/* Root redirects to active shell */}
      <Route index element={<ShellRedirect />} />

      {/* Core shell (platform_admin with global/core=true) */}
      <Route
        path="/admin/*"
        element={
          <ShellGuard requiredShell="admin">
            <Suspense fallback={<LoadingSpinner />}>
              <AdminRoutes />
            </Suspense>
          </ShellGuard>
        }
      />

      {/* Ops shell (operators, org admins, etc.) */}
      <Route
        path="/ops/*"
        element={
          <ShellGuard requiredShell="ops">
            <Suspense fallback={<LoadingSpinner />}>
              <OpsRoutes />
            </Suspense>
          </ShellGuard>
        }
      />

      {/* Client shell (client-facing portal) */}
      <Route
        path="/client/*"
        element={
          <ShellGuard requiredShell="client">
            <Suspense fallback={<LoadingSpinner />}>
              <ClientRoutes />
            </Suspense>
          </ShellGuard>
        }
      />

      {/* Legacy routes: redirect old top-level paths to admin shell for backward compat */}
      <Route
        path="/dashboard"
        element={<Navigate to="/admin/dashboard" replace />}
      />
      <Route
        path="/catalog/*"
        element={<Navigate to="/admin/catalog" replace />}
      />
      <Route
        path="/definitions/*"
        element={<Navigate to="/admin/catalog" replace />}
      />
      <Route
        path="/capabilities/*"
        element={<Navigate to="/admin/catalog" replace />}
      />
      <Route
        path="/operational-contracts/*"
        element={<Navigate to="/admin/catalog" replace />}
      />
      <Route
        path="/projects/*"
        element={<Navigate to="/admin/projects" replace />}
      />
      <Route
        path="/twins/*"
        element={<Navigate to="/ops/projects" replace />}
      />
      <Route
        path="/provisioning/*"
        element={<Navigate to="/admin/provisioning" replace />}
      />
      <Route
        path="/simulator/*"
        element={<Navigate to="/admin/simulator" replace />}
      />
      <Route
        path="/operations/*"
        element={<Navigate to="/admin/operations" replace />}
      />
      <Route
        path="/commissioning/*"
        element={<Navigate to="/admin/commissioning" replace />}
      />
      <Route
        path="/garage/*"
        element={<Navigate to="/admin/garage" replace />}
      />
      <Route
        path="/runtime/*"
        element={<Navigate to="/admin/runtime" replace />}
      />
      <Route path="/geo/*" element={<Navigate to="/admin/geo" replace />} />
      <Route
        path="/settings"
        element={<Navigate to="/admin/settings" replace />}
      />

      {/* 404 catch-all handled in App.tsx */}
      <Route path="*" element={<ShellRedirect />} />
    </Routes>
  );
}
